import React, { useCallback, useEffect, useRef } from 'react';
import { Trans, withI18n } from '@lingui/react';
import { useSelector } from 'react-redux';
import { Field } from 'react-final-form';
import without from 'lodash/without';
import get from 'lodash/get';

import PropertiesTreeWidget from '../../../../components/utils/PropertiesTreeWidget';
import SelectedPropertiesList from '../SelectedPropertiesList';
import { selectors as appSelectors } from '../../../../reducers/appReducer';
import { getLocaleFromLanguageCode } from '../../../../intl-helpers';
import { getAllProductProperties } from '../../../../api';
import { buildPropertyMappings, sortByTreeOrder } from '../../utils/propertyHelpers';

/**
 * Editor regole profilo con layout a due colonne stile customize-table.
 * A sinistra: PropertiesTreeWidget con checkbox (carica proprieta dall'API).
 * A destra: lista selezionate con drag-and-drop e rimozione.
 */
const ProfileRulesEditor = ({ formValues, form, i18n, showParentNodes = false }) => {
    const { ruleStrategy = 'include_all', selectedProperties = [] } = formValues;
    const productProperties = useSelector(appSelectors.getProductProperties);
    const mappingsRef = useRef({ childrenOf: {}, depthOf: {}, treeIndexOf: {} });

    // Carica le mappature parent->children e code->depth
    useEffect(() => {
        getAllProductProperties().then(({ data }) => {
            mappingsRef.current = buildPropertyMappings(data);
        });
    }, []);

    const handleTreeChange = useCallback((data) => {
        const { selectedNodes, op } = data;
        const previous = formValues.selectedProperties || [];
        const { childrenOf, treeIndexOf } = mappingsRef.current;

        // 1. Filtra valori falsy (il nodo root virtuale dell'InfiniteTree ha code: undefined)
        let newSelection = selectedNodes.filter(Boolean);

        if (op === 'deselect') {
            // Il widget rimuove automaticamente i padri quando l'ultima foglia viene deselezionata.
            // Per i profili vogliamo che ogni nodo sia indipendente: riaggiungiamo i padri
            // che sono stati rimossi ma hanno ancora almeno un figlio selezionato.
            const removed = previous.filter(code => code && !newSelection.includes(code));

            const toKeep = removed.filter(code => {
                const children = childrenOf[code];
                if (!children) return false;
                return children.some(childCode => newSelection.includes(childCode));
            });

            newSelection = [...newSelection, ...toKeep];
        }

        // 2. Ordina nell'ordine in cui appaiono nell'albero
        form.change('selectedProperties', sortByTreeOrder(newSelection, treeIndexOf));
    }, [form, formValues.selectedProperties]);

    const handleRemoveProperty = useCallback((property) => {
        const current = formValues.selectedProperties || [];
        let newSelection = without(current, property);

        // Rimuovi i nodi padre che non hanno piu figli selezionati
        const { childrenOf } = mappingsRef.current;
        let changed = true;
        while (changed) {
            changed = false;
            newSelection = newSelection.filter(code => {
                const children = childrenOf[code];
                if (!children) return true;
                if (!children.some(c => newSelection.includes(c))) {
                    changed = true;
                    return false;
                }
                return true;
            });
        }

        form.change('selectedProperties', newSelection);
    }, [form, formValues.selectedProperties]);

    const getPropertyLabel = (code) => {
        const langCode = getLocaleFromLanguageCode(i18n.language);
        if (productProperties[code]) {
            return get(productProperties[code], `label.${langCode}`, code);
        }
        return code;
    };

    const showPropertySelector = ruleStrategy === 'include_specific';

    const { childrenOf } = mappingsRef.current;
    const visibleProperties = showParentNodes
        ? selectedProperties
        : selectedProperties.filter(code => !childrenOf[code]);

    return (
        <div>
            {/* Strategia */}
            <div className="form-group mb-4 d-flex items-center">
                <label className="form-label mr-5">
                    <Trans id="profiles:rules:rule_to_choose" />
                </label>

                <div className="my-1">
                    <label className="form-radio">
                        <Field
                            name="ruleStrategy"
                            component="input"
                            type="radio"
                            value="include_all"
                        />
                        <i className="form-icon" />
                        <Trans id="profiles:rules:include_all" />
                    </label>
                </div>

                <div className="my-1">
                    <label className="form-radio">
                        <Field
                            name="ruleStrategy"
                            component="input"
                            type="radio"
                            value="include_specific"
                        />
                        <i className="form-icon" />
                        <Trans id="profiles:rules:include_specific" />
                    </label>
                </div>

            </div>

            {/* Selettore proprieta a due colonne (stile customize-table) */}
            {showPropertySelector && (
                <div className="grid grid-cols-12 gap-2">
                    {/* Colonna sinistra: albero proprieta con checkbox */}
                    <div className="col-span-12 lg:col-span-6">
                        <PropertiesTreeWidget
                            selectOnlyLeaf={false}
                            selectedNodes={selectedProperties}
                            onChange={handleTreeChange}
                            maxHeight={false}
                            autoHeight={false}
                            excludeRoots={['old_assets', 'ordinamenti']}
                        />
                    </div>

                    {/* Colonna destra: proprieta selezionate, ordinate per position (come nella scheda articolo) */}
                    <div className="col-span-12 lg:col-span-6">
                        <p className="text-gray text-sm mb-2">
                            <Trans id="profiles:rules:order_hint" />
                        </p>
                        <SelectedPropertiesList
                            properties={visibleProperties}
                            getLabel={getPropertyLabel}
                            onRemove={handleRemoveProperty}
                            removeTitle={i18n._('profiles:rules:remove')}
                        />
                    </div>
                </div>
            )}

            {/* Errore validazione proprieta */}
            <Field name="selectedProperties" subscription={{ error: true, touched: true }}>
                {({ meta }) =>
                    meta.error && meta.touched ? (
                        <div className="text-error text-sm mt-2">
                            <Trans id={meta.error} />
                        </div>
                    ) : null
                }
            </Field>
        </div>
    );
};

export default withI18n()(ProfileRulesEditor);
